import EventLog from "@mirage/analytics/components/EventLog";
import { useComponentConfig } from "@mirage/component-config";
import { ChatPageWrapper } from "@mirage/mosaics/Chat/components/layouts/ChatPageLayout";
import { SourcesCacheProvider } from "@mirage/mosaics/ComposeAssistant/components/workflow-agent/SourcesCacheContext";
import { AssistTemplatesContextProvider } from "@mirage/mosaics/ComposeAssistant/data/AssistTemplatesContext";
import { ComposeAnalyticsContextProvider } from "@mirage/mosaics/ComposeAssistant/data/ComposeAnalyticsContext";
import { ComposeSessionsContextProvider } from "@mirage/mosaics/ComposeAssistant/data/ComposeSessionsContext";
import { ComposeVoicesContextProvider } from "@mirage/mosaics/ComposeAssistant/data/ComposeVoicesContext";
import { ComposeCurrentSessionContextProvider } from "@mirage/mosaics/ComposeAssistant/data/current-session/ComposeCurrentSessionContext";
import { useConversationEventOrchestrator } from "@mirage/mosaics/ConversationAssistant/hooks/useConversationEventOrchestrator";
import type { ConversationRouterState } from "@mirage/service-conversation/router_state_types";
import { readyPage, updateConversation } from "@mirage/service-fss-bridge";
import { useActionEvents } from "@mirage/service-fss-bridge/hooks/bridgeObservers";
import { useMountEffect } from "@react-hookz/web";
import { useCallback, useEffect, useRef } from "react";
import { useDIGModalDetection } from "../hooks/useDigModalDetection";
import { ConversationAssistantPageContent } from "./ConversationAssistantPageContent";

export const EmbedChatPage: React.FC = () => {
  useDIGModalDetection();
  const { analyticsLogsOpen, toggleAnalyticsLogs } = useComponentConfig();
  const event = useActionEvents(["open.pap-event-log", "close.pap-event-log"]);

  useEffect(() => {
    if (event?.source === "open.pap-event-log" && !analyticsLogsOpen) {
      toggleAnalyticsLogs();
    } else if (event?.source === "close.pap-event-log" && analyticsLogsOpen) {
      toggleAnalyticsLogs();
    }
  }, [event, analyticsLogsOpen, toggleAnalyticsLogs]);

  const createNewSession = useRef<
    (routerState?: ConversationRouterState) => void
  >(() => {});

  const handleRequestNewSession = useCallback(
    (routerState?: ConversationRouterState) => {
      createNewSession.current(routerState);
    },
    [],
  );

  const handleConversationIdChange = useCallback(
    (conversationId: string | undefined) => {
      updateConversation(conversationId ?? "");
    },
    [],
  );

  useConversationEventOrchestrator({
    onRequestNewSession: handleRequestNewSession,
  });

  useMountEffect(() => {
    readyPage();
  });

  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ComposeSessionsContextProvider>
        <ComposeAnalyticsContextProvider>
          <ComposeVoicesContextProvider>
            <AssistTemplatesContextProvider>
              <ChatPageWrapper>
                <ComposeCurrentSessionContextProvider>
                  <SourcesCacheProvider>
                    <ConversationAssistantPageContent
                      showModelPickerButton={false}
                      showIncognitoButton={false}
                      onConversationIdChange={handleConversationIdChange}
                      onRequestNewSession={(handler) =>
                        (createNewSession.current = handler)
                      }
                      shouldAutoFocusInput={false}
                    />
                  </SourcesCacheProvider>
                </ComposeCurrentSessionContextProvider>
              </ChatPageWrapper>
            </AssistTemplatesContextProvider>
          </ComposeVoicesContextProvider>
        </ComposeAnalyticsContextProvider>
      </ComposeSessionsContextProvider>
      {analyticsLogsOpen && <EventLog />}
    </div>
  );
};
